import { motion } from "framer-motion";
import { Droplets, Search, Sparkles, Shield, Camera } from "lucide-react";
import { FadeUp } from "@/components/AnimatedSection";

const steps = [
  { icon: Search, title: "Inspection", description: "We walk around documenting every angle and noting problem areas.", step: "01" },
  { icon: Droplets, title: "Pre-Wash", description: "Thorough pre-wash to loosen dirt and debris, followed by a full rinse.", step: "02" },
  { icon: Droplets, title: "Contact Wash", description: "Premium foam soap with a soft mitt, carefully hand-washing every panel.", step: "03" },
  { icon: Droplets, title: "Rinse", description: "Complete rinse removing all soap and remaining contaminants.", step: "04" },
  { icon: Shield, title: "Protection", description: "Ceramic sealant or spray wax applied to lock in shine.", step: "05" },
  { icon: Droplets, title: "Hand Dry", description: "Meticulous hand drying with plush microfiber for a streak-free finish.", step: "06" },
  { icon: Sparkles, title: "Dressing", description: "Interior and exterior dressings plus a deep tire shine.", step: "07" },
  { icon: Camera, title: "Final Photos", description: "Stunning after shots to showcase the transformation.", step: "08" },
];

const ProcessTimeline = () => (
  <section className="section-padding bg-background overflow-hidden relative">
    <div className="absolute inset-0 bg-dots opacity-10" />
    <div className="absolute inset-0 mesh-gradient" />
    <div className="orb-glow orb-gold w-[500px] h-[500px] top-0 left-1/2 opacity-8 orb-pulse" />
    
    <div className="container-luxury relative">
      <FadeUp className="text-center mb-20">
        <span className="badge-shimmer mb-4 inline-flex">How It Works</span>
        <h2 className="font-display text-3xl md:text-5xl font-bold">
          Our <span className="gold-gradient-text">Process</span>
        </h2>
        <p className="text-muted-foreground mt-4 max-w-lg mx-auto text-sm leading-relaxed">
          Every detail follows our proven 8-step process for consistent, showroom results.
        </p>
      </FadeUp>

      {/* Desktop */}
      <div className="hidden md:block">
        <div className="relative">
          <motion.div
            className="absolute top-[56px] left-[8%] right-[8%] h-px"
            style={{ background: "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.2), hsl(var(--primary) / 0.3), hsl(var(--primary) / 0.2), transparent)" }}
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          />
          {/* Glow on the line */}
          <motion.div
            className="absolute top-[55px] left-[8%] right-[8%] h-[3px] blur-sm"
            style={{ background: "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.15), transparent)" }}
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
          />
          <div className="grid grid-cols-4 lg:grid-cols-8 gap-4">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                className="text-center relative group"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 0.12 * i, ease: [0.25, 0.4, 0.25, 1] }}
              >
                <div className="relative inline-block mb-5">
                  <motion.div
                    className="w-[104px] h-[104px] rounded-2xl frosted-card flex flex-col items-center justify-center gap-1.5 mx-auto relative z-10 group-hover:border-primary/30 transition-all duration-500"
                    whileHover={{ scale: 1.08, y: -4, transition: { duration: 0.3 } }}
                  >
                    <span className="text-[9px] font-bold tracking-[0.3em] text-primary/50 uppercase font-body">{step.step}</span>
                    <step.icon size={26} className="text-primary transition-all duration-300 group-hover:scale-110 group-hover:drop-shadow-[0_0_8px_hsl(var(--gold)/0.3)]" />
                  </motion.div>
                  <div className="absolute inset-0 rounded-2xl bg-primary/5 scale-125 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
                </div>
                <h3 className="font-display text-[15px] font-semibold mb-1.5 group-hover:text-primary transition-colors duration-300">{step.title}</h3>
                <p className="text-muted-foreground text-[11px] leading-relaxed max-w-[180px] mx-auto">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden relative">
        <motion.div
          className="absolute left-6 top-0 bottom-0 w-px"
          style={{ background: "linear-gradient(to bottom, transparent, hsl(var(--primary) / 0.3), transparent)" }}
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        />
        <div className="space-y-8">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              className="relative flex items-start gap-5 pl-16"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.1 * i }}
            >
              <div className="absolute left-0 z-10">
                <motion.div className="w-12 h-12 rounded-xl gold-gradient-bg flex items-center justify-center gold-glow" whileHover={{ scale: 1.1, rotate: 5 }}>
                  <step.icon size={20} className="text-primary-foreground" />
                </motion.div>
              </div>
              <div>
                <span className="text-[10px] font-bold tracking-widest text-primary/60 uppercase">{step.step}</span>
                <h3 className="font-display text-lg font-semibold mb-1">{step.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default ProcessTimeline;
